import React from 'react';
import { Box, TextField, Typography, InputBase } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTextInputHot } from './useTextInputHot';
import { MODE } from './config';
import { spacing, radius, height, colors } from 'common/theme';
import { FontFamily, typographyScale, TypographyVariant } from 'common/theme';
import { widths } from 'common/theme/tokens/typography/typography';

export { MODE };

export const TextInputHot = (props) => {
  const {
    placeholder,
    value,
    error,
    disabled,
    mode = MODE.OUTLINED,
    label,
    helperText,
    sx,
    inputProps,
    testID,
    ...rest
  } = props;

  const theme = useTheme();
  const {
    labelColor,
    handleFocus,
    handleBlur,
    isFocused,
    shouldFloat,
    labelBackgroundColor,
  } = useTextInputHot(props);

  if (mode === MODE.FORM) {
    return (
      <Box
        testId={testID}
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: `${spacing.xs}px`,
          mb: { xs: `${spacing.xl}px`, md: `${spacing.md}px` },
          ...(disabled && {
            filter: 'grayscale(100%)',
            opacity: 0.7,
          }),
          ...sx,
        }}
      >
        { label && (
          <Typography
          sx={{
            fontFamily: FontFamily.regular,
              fontSize: typographyScale[TypographyVariant.BodyLarge].fontSize,
              lineHeight: typographyScale[TypographyVariant.BodyLarge].lineHeight,
              color: theme.palette.text.subtle,
            }}
          >
            {label || placeholder}
          </Typography>
        )}
        <InputBase
          {...rest}
          value={value}
          disabled={disabled}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          inputProps={{
            ...(inputProps || {}),
            ...(testID ? {
              testid: `${testID}-input`,
            } : {}),
          }}
          sx={{
            width: widths.full,
            height: height.input,
            fontFamily: FontFamily.regular,
            fontSize: typographyScale[TypographyVariant.BodyLarge].fontSize,
            padding: `${spacing.md}px`,
            borderRadius: `${radius.md}px`,
            border: `1px solid ${
              error
                ? theme.palette.error.main
                : isFocused
                ? colors.palette.blue550
                : colors.palette.blueAlpha200
            }`,
            backgroundColor: colors.palette.white,
            transition: 'border-color 0.2s ease',
            '&:hover': {
              borderColor: !disabled && colors.palette.blue550,
            },
            '&.Mui-disabled': {
              backgroundColor: 'transparent',
            },
          }}
        />
        {helperText && (
          <Typography
            variant="caption"
            sx={{
              color: error ? theme.palette.error.main : theme.palette.text.subtle,
              display: 'block',
              marginTop: error ? `${spacing.xs}px` : 0,
            }}
          >
            {helperText}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box
      testId={testID}
      sx={{
        position: 'relative',
        width: '100%',
        ...sx,
      }}
    >
      <TextField
        {...rest}
        label={label || placeholder}
        value={value}
        error={error}
        disabled={disabled}
        onFocus={handleFocus}
        onBlur={handleBlur}
        helperText={helperText}
        variant={mode === MODE.FLAT ? 'filled' : 'outlined'}
        fullWidth
        inputProps={{
          ...(rest.inputProps || {}),
          ...(testID ? {
            testid: `${testID}-input`,
          } : {}),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: `${radius.sm}px`,
          },
          '& .MuiFilledInput-root': {
            borderRadius: `${radius.sm}px ${radius.sm}px 0 0`,
          },
          '& .MuiInputLabel-root': {
            color: labelColor,
          },
          '& .MuiInputLabel-shrink': {
            backgroundColor: labelBackgroundColor,
            paddingLeft: spacing.xs,
            paddingRight: spacing.xs,
          },
        }}
      />
    </Box>
  );
};

export default TextInputHot;
